import React, { useState } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize Google Generative AI with API key
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

const SWPCalculator = () => {
  const [totalInvestment, setTotalInvestment] = useState('');
  const [withdrawalPerMonth, setWithdrawalPerMonth] = useState('');
  const [expectedReturnRate, setExpectedReturnRate] = useState('');
  const [totalPeriod, setTotalPeriod] = useState('');
  const [remainingAmount, setRemainingAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState('₹');
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

  const calculateSWP = () => {
    let investment = parseFloat(totalInvestment) || 0;
    let withdrawal = parseFloat(withdrawalPerMonth) || 0;
    let returnRate = parseFloat(expectedReturnRate) || 0;
    let period = parseFloat(totalPeriod) || 0;

    if (investment <= 0 || withdrawal <= 0 || returnRate <= 0 || period <= 0) {
      setError('Please enter valid values for all fields.');
      setRemainingAmount('');
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setError('');
      }, 1500);
      return;
    }

    setLoading(true);
    setError('');
    setRemainingAmount('');

    setTimeout(() => {
      const monthlyInterestRate = (returnRate / 100) / 12;
      const totalMonths = period * 12;
  
      let finalValue = investment;
      for (let i = 0; i < totalMonths; i++) {
        finalValue = finalValue * (1 + monthlyInterestRate) - withdrawal;
        if (finalValue < 0) {
          finalValue = 0;
          break;
        }
      }
  
      setRemainingAmount(finalValue.toFixed(2));
      setLoading(false);
      
      // Get AI Analysis
      getAIAnalysis(investment, withdrawal, returnRate, period, finalValue);
    }, 1500);
  };

  const getAIAnalysis = async (investment, monthlyWithdrawal, returnRate, period, finalValue) => {
    setIsLoadingAnalysis(true);
    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

      const prompt = `As a financial advisor, analyze this Systematic Withdrawal Plan (SWP):
Initial Investment: ₹${investment.toLocaleString()}
Monthly Withdrawal: ₹${monthlyWithdrawal.toLocaleString()}
Expected Return Rate: ${returnRate}% per annum
Time Period: ${period} years
Remaining Amount: ₹${finalValue.toLocaleString()}

Provide 4 key points (one line each):
1. Sustainability analysis (will the money last?)
2. Withdrawal rate assessment (is it too aggressive?)
3. Return expectations vs market conditions
4. Suggestions for portfolio rebalancing`;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      setAiAnalysis(response.text());
    } catch (error) {
      console.error("Error getting AI analysis:", error);
      setAiAnalysis("Unable to generate analysis at this time. Please try again.");
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const clearAll = () => {
    setTotalInvestment('');
    setWithdrawalPerMonth('');
    setExpectedReturnRate('');
    setTotalPeriod('');
    setRemainingAmount('');
    setError(''); 
    setLoading(false);
    setAiAnalysis('');
    setIsLoadingAnalysis(false);
  };

  const currencyChange = (e) => {
    setCurrency(e.target.value);
  };

  return (
    <div className="bg-[#ffffff] py-6 sm:py-12 px-4 sm:px-6 lg:px-8 font-sans relative overflow-hidden min-h-screen">
      {/* Dotted Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
            backgroundSize: '20px 20px sm:30px sm:30px',
            backgroundPosition: '0 0'
          }}
        />
      </div>
      
      {/* Vertical Lines */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(10)].map((_, i) => (
          <div
            key={`v-${i}`}
            className="absolute h-full border-l border-dotted border-black"
            style={{ left: `${i * 10}%` }}
          />
        ))}
      </div>
      
      {/* Horizontal Lines */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(8)].map((_, i) => (
          <div
            key={`h-${i}`}
            className="absolute w-full border-t border-dotted border-black"
            style={{ top: `${i * 12.5}%` }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="mb-6 sm:mb-10 text-left">
          <h2 className="text-4xl sm:text-6xl font-black tracking-tight text-[#000000] leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
            <span className="block italic">SYSTEMATIC</span>
            <span className="block text-[#f5f5f5] italic" style={{ textShadow: '2px 2px 0px #000000' }}>WITHDRAWAL</span>
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-700 max-w-xl font-medium">
            Plan and manage regular withdrawals from your investment corpus for a steady income stream.
          </p>
          <div className="mt-3 sm:mt-4 flex items-center gap-3 sm:gap-4">
            <div className="bg-black text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold">
              SWP Calculator
            </div>
            <div className="text-xl sm:text-2xl">*</div>
          </div>
        </div>

        {/* Main Calculator Card */}
        <div 
          className="rounded-[12px] sm:rounded-[16px] overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 sm:hover:-translate-y-2"
          style={{
            backgroundColor: "#f5f5f5",
            color: "#000000",
            border: "2px solid #e0e0e0",
            fontFamily: 'Arial, sans-serif'
          }}
        >
          <div className="p-4 sm:p-6 relative">
            <h3 className="text-xl sm:text-2xl font-black tracking-tight uppercase mb-3 sm:mb-4 text-center">Systematic Withdrawal Plan Calculator</h3>
            <p className="text-black/80 mb-4 sm:mb-6 text-center font-medium text-sm sm:text-base">
              The SWP calculator helps investors plan and manage regular withdrawals from their investment corpus.
            </p>
            
            <div className="page-container">
              {/* Currency Selection */}
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <label className="text-black font-black tracking-wide uppercase text-xs sm:text-sm mr-3 sm:mr-4">Currency:</label>
                <select
                  className="form-select bg-white/80 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-2 focus:ring-2 focus:ring-black/30 focus:border-black/40 font-bold text-sm sm:text-base"
                  onChange={currencyChange}
                  value={currency}
                >
                  <option value="₹">₹ INR</option>
                  <option value="$">$ USD</option>
                </select>
              </div>

              <div className="input-container space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor='investment2' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Total Investment:</label>
                  <input
                    type="number"
                    value={totalInvestment}
                    id='investment2'
                    onChange={(e) => setTotalInvestment(e.target.value)}
                    className="form-input block w-full bg-white/80 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter total investment amount"
                    required
                  />
                </div>
                <div>
                  <label htmlFor='withdraw' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Withdrawal Per Month:</label>
                  <input
                    type="number"
                    value={withdrawalPerMonth}
                    id='withdraw'
                    onChange={(e) => setWithdrawalPerMonth(e.target.value)}
                    className="form-input block w-full bg-white/80 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter withdrawal per month"
                    required
                  />
                </div>
                <div>
                  <label htmlFor='return2' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Expected Return Rate (%):</label>
                  <input
                    type="number"
                    value={expectedReturnRate}
                    id='return2'
                    onChange={(e) => setExpectedReturnRate(e.target.value)}
                    className="form-input block w-full bg-white/80 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter expected return rate"
                    required
                  />
                </div>
                <div>
                  <label htmlFor='period' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Total Period (Years):</label>
                  <input
                    type="number"
                    value={totalPeriod}
                    id='period'
                    onChange={(e) => setTotalPeriod(e.target.value)}
                    className="form-input block w-full bg-white/80 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter time period in years"
                    required
                  />
                </div>
              </div>

              <div className="btn-calculate flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-4 sm:mt-6">
                <button
                  onClick={calculateSWP}
                  className="bg-black text-white font-black py-3 px-6 rounded-lg sm:rounded-xl hover:bg-gray-800 transition-colors duration-200 tracking-wide uppercase text-sm sm:text-base w-full sm:w-auto"
                >
                  Calculate →
                </button>
                <button
                  onClick={clearAll}
                  className="bg-transparent border-2 border-black text-black font-black py-3 px-6 rounded-lg sm:rounded-xl hover:bg-black hover:text-white transition-colors duration-200 tracking-wide uppercase text-sm sm:text-base w-full sm:w-auto"
                >
                  Clear
                </button>
              </div>

              <div 
                className="result-container bg-black/10 backdrop-blur-sm border-2 border-black/20 p-4 sm:p-6 mt-4 sm:mt-6 rounded-lg sm:rounded-xl relative" 
                style={{ minHeight: '160px', maxHeight: '200px' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-black border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="text-black font-bold">
                    <div className="mb-3 sm:mb-4 text-lg sm:text-xl font-black tracking-wide uppercase">Result:</div>
                    {error && <div className="text-red-600 mb-3 sm:mb-4 font-black text-base sm:text-lg">{error}</div>}
                    {remainingAmount && !error && (
                      <div className="text-base sm:text-lg flex flex-col sm:flex-row sm:items-center">
                        <span className="font-black uppercase tracking-wide">Remaining Amount:</span> 
                        <span className="text-xl sm:text-2xl font-black sm:ml-2">{currency}{parseFloat(remainingAmount).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Background Pattern */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none rounded-lg sm:rounded-xl opacity-5">
                  <div 
                    className="w-full h-full"
                    style={{
                      backgroundImage: 'linear-gradient(45deg, currentColor 25%, transparent 25%), linear-gradient(-45deg, currentColor 25%, transparent 25%)',
                      backgroundSize: '8px 8px sm:10px sm:10px',
                      backgroundPosition: '0 0, 0 4px sm:0 5px'
                    }}
                  />
                </div>
              </div>

              {/* AI Analysis Section */}
              {(aiAnalysis || isLoadingAnalysis) && (
                <div className="mt-6 bg-black/10 p-6 rounded-xl border-2 border-black/20 shadow-lg">
                  <div className="mb-4">
                    <h3 className="text-xl sm:text-2xl font-black text-black tracking-wide uppercase">SWP Analysis</h3>
                  </div>
                  {isLoadingAnalysis ? (
                    <div className="flex items-center gap-3 text-black">
                      <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full"></div>
                      <span className="font-bold">Analyzing your withdrawal plan...</span>
                    </div>
                  ) : (
                    <div className="max-w-none">
                      <p className="text-black text-base sm:text-lg font-bold leading-relaxed whitespace-pre-line">{aiAnalysis}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 opacity-20">
              <div className="w-8 h-8 sm:w-12 sm:h-12 border-2 sm:border-4 border-black rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SWPCalculator;